import { z } from 'zod';

// ─── CBT module catalogue ───────────────────────────────────────────────────
//
// The modules whose progress the server stores, with their ordered step keys.
// Ids and order only, never copy: module content is served separately, and the
// keys are a stable contract across that move — renaming one orphans every
// stored step row and completion keyed by it.
//
// Keys are condition-agnostic (ADR-015). Essentials 1's step keys are the
// 1.0 journey step ids it was built from, which the member app's showcase
// fixture already uses; the 1.0 stage and step numbers themselves live only in
// the legacy import's config.

export const MODULE_CATALOGUE = {
  'essentials-1': {
    stepKeys: [
      'step-82',
      'step-83',
      'step-84',
      'step-85',
      'step-86',
      'step-87',
      'step-88',
      'step-89',
      'step-90',
      'step-91',
      'step-92',
      'step-93',
    ],
  },
} as const satisfies Record<string, { stepKeys: readonly string[] }>;

export type ModuleKey = keyof typeof MODULE_CATALOGUE;
export type ModuleStepKey<K extends ModuleKey = ModuleKey> =
  (typeof MODULE_CATALOGUE)[K]['stepKeys'][number];

/** Every catalogued module key; the type check below keeps it equal to the catalogue. */
export const MODULE_KEYS = ['essentials-1'] as const satisfies readonly ModuleKey[];
type MissingModuleKey = Exclude<ModuleKey, (typeof MODULE_KEYS)[number]>;
const everyModuleKeyListed: MissingModuleKey extends never ? true : never = true;
void everyModuleKeyListed;

export const moduleKeySchema = z.enum(MODULE_KEYS);

export function isModuleKey(value: string): value is ModuleKey {
  return Object.hasOwn(MODULE_CATALOGUE, value);
}

/** The ordered step keys of a module. */
export function moduleStepKeys(moduleKey: ModuleKey): readonly string[] {
  return MODULE_CATALOGUE[moduleKey].stepKeys;
}

export function isModuleStepKey(moduleKey: ModuleKey, stepKey: string): boolean {
  return moduleStepKeys(moduleKey).includes(stepKey);
}

/**
 * Where a step row came from. `app`: the member completed it in the app.
 * `legacy_1_0`: imported from the 1.0 `user_steps` history.
 */
export const MODULE_STEP_SOURCE = ['app', 'legacy_1_0'] as const;
export const moduleStepSourceSchema = z.enum(MODULE_STEP_SOURCE);
export type ModuleStepSource = z.infer<typeof moduleStepSourceSchema>;

/**
 * Where a completion came from. `app`: written by the server when the member's
 * last step landed. `staff_recorded`: a care-team user recorded it with a date.
 * `legacy_1_0`: imported from the 1.0 `user_stages` history.
 */
export const MODULE_COMPLETION_SOURCE = ['app', 'legacy_1_0', 'staff_recorded'] as const;
export const moduleCompletionSourceSchema = z.enum(MODULE_COMPLETION_SOURCE);
export type ModuleCompletionSource = z.infer<typeof moduleCompletionSourceSchema>;

/** A module's effective status for one member. */
export const MODULE_PROGRESS_STATUS = ['not_started', 'in_progress', 'complete'] as const;
export const moduleProgressStatusSchema = z.enum(MODULE_PROGRESS_STATUS);
export type ModuleProgressStatus = z.infer<typeof moduleProgressStatusSchema>;
